import * as React from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Card from "@material-ui/core/Card";
import { CardContent } from "@material-ui/core";

import Divider from "@material-ui/core/Divider";

//icons
import UploadIcon from "mdi-material-ui/Upload";
import ViewIcon from "@material-ui/icons/RemoveRedEye";
import HistoryIcon from "@material-ui/icons/History";

// jaza-soft
import {
  PageFooter,
  PageHeader,
  SimpleShowLayout,
  TextField,
  FunctionField,
  BackButton,
  EditButton,
  DeleteButton,
  Button,
  showNotification
} from "jazasoft";
import { minValue, required } from "@jazasoft/mui-table";

// local
import CardHeader from "../../../components/CardHeader";
import MuiTable from "../../../components/MuiTable";

import { fieldOptions, getDistinctObjectValues, getDistinctValues, isEmpty, isFalse, isTrue, listFromCsv, toFixed } from "../../../utils/helpers";
import { getWhFlowInfoList } from "../../../helpers/orderHelper";
import useQuery from "../../../hooks/useQuery";
import useDeepMemo from "../../../hooks/useDeepMemo";
import hasPrivilege from "../../../utils/hasPrivilege";
import useSafeState from "../../../hooks/useSafeState";
import useMutation from "../../../hooks/useMutation";
import useDeepEffect from "../../../hooks/useDeepEffect";
import PackingListDrawer from "./PackingListDrawer";
import { useEntityPrivilege, useSetting, useTime } from "../../../hooks";
import { formatDate } from "../../../utils/dateUtils";
import SettingKeys from "../../../utils/SettingKeys";
import { Entity, LedgerType, Role } from "../../../utils/types";
import { ResourceId, Scope } from "../../../utils/privilege";
import useWarehouse from "../../../hooks/useWarehouse";
import { UOM } from "../../../constants";
import { convert } from "../../../helpers/unitConversionHelper";
import BlanketIssueDrawer from "./BlanketIssueDrawer";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const setFormValues = (whFlowInfoList, name, form, row) => {
  const productMap = whFlowInfoList.map(flowInfo => flowInfo.product).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
  const productList = Object.values(productMap);

  if (productList.length === 1) {
    const product = productList[0];
    form.change(`${name.split(".")[0]}.productId`, product.id);
  } else if (!Object.keys(productMap).includes(`${row.productId}`)) {
    form.change(`${name.split(".")[0]}.productId`, null);
  }

  const styles = getDistinctValues(whFlowInfoList.map(flowInfo => flowInfo.style));
  if (styles.length === 1) {
    form.change(`${name.split(".")[0]}.style`, styles[0]);
  } else if (styles.includes(row.style)) {
    form.change(`${name.split(".")[0]}.style`, null);
  }

  const colors = getDistinctValues(whFlowInfoList.map(flowInfo => flowInfo.color));
  if (colors.length === 1) {
    form.change(`${name.split(".")[0]}.color`, colors[0]);
  } else if (!colors.includes(row.color)) {
    form.change(`${name.split(".")[0]}.color`, null);
  }
};

const effectiveColor = (flowInfo, isLabel = false) =>
  isLabel
    ? `${isEmpty(flowInfo?.parentColor) || flowInfo?.parentColor === flowInfo?.color ? "" : `${flowInfo.parentColor}, `}${flowInfo?.color}`
    : `${isEmpty(flowInfo?.parentColor) ? "" : `${flowInfo.parentColor}, `}${flowInfo?.color}`;

const partMapper = p => ({ id: p.effPartName, name: p.effPartName });

const useStyles = makeStyles(theme => ({
  content: {
    margin: theme.spacing(3),
    marginTop: "0.5em"
  },
  button: {
    margin: "0 1em"
  },
  marginVertical: {
    marginTop: "1em",
    marginBottom: "2em"
  },
  pageFooter: {
    "&>*": {
      marginRight: "1em"
    }
  },
  drawerHeader: {
    display: "flex",
    width: 600,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  }
}));

const InvoiceView = props => {
  const classes = useStyles();
  const { id, i18nKey, basePath, resource, history, roles, hasAccess } = props;

  const { disableEdit, disableDelete } = useEntityPrivilege("GRN");
  const { disableEdit: disableBIEdit } = useEntityPrivilege("BlanketIssue");

  const [vesrion, setVersion] = useSafeState(0);
  const [drawerState, setDrawerState] = useSafeState({ open: false, fabricItem: null });
  const [biDrawerState, setBiDrawerState] = useSafeState({ open: false, fabricItem: null });
  const [selectedKey, setSelectedKey] = useSafeState(null);
  const [associationColumns, setAssociationColumns] = useSafeState([]);
  const [associationRows, setAssociationRows] = useSafeState([]);
  const [associating, setAssociating] = useSafeState(false); // Whether association is in progress or not, When association is in progress, restrict Item switch
  const [orders, setOrders] = useSafeState([]);

  const dispatch = useDispatch();
  const [mutate] = useMutation();
  const { utc_offset } = useTime();
  const { allWhList } = useWarehouse();

  const invoiceFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.INVOICES);
  const fabricItemFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRIC_ITEMS);
  const conversionFactor = useSetting(SettingKeys.UOM_CONVERSION_FACTOR);
  const conversionFactorList = JSON.parse(conversionFactor || "[]");

  const { data: invoice = {} } = useQuery(id && { url: `invoices/${id}`, vesrion });

  const fabricIds = getDistinctValues(invoice?.fabricItemList?.map(fi => fi.fabric?.id) || []);
  const { data: fabrics = [] } = useQuery(
    !isEmpty(fabricIds) && { url: "fabrics", params: { search: `id=in=(${fabricIds.join(",")})`, size: 10000 } }
  );
  const { data: ledgerList = [] } = useQuery(
    !isEmpty(selectedKey) && { url: "ledgers", params: { search: `fabricItem.id==${selectedKey}`, tab: "GRN", size: 10000, vesrion } }
  );
  const { data: invLedgerList = [] } = useQuery(
    !isEmpty(id) && { url: "ledgers", params: { search: `invoice.id==${id}`, tab: "GRN", size: 2000, vesrion } }
  );

  const orderIds = getDistinctValues(ledgerList?.filter(e => !isEmpty(e.orderId)).map(e => e.orderId));
  const { data: orderData = [] } = useQuery(
    !isEmpty(orderIds) && {
      url: "orders",
      params: { search: `id=in=(${orderIds.join(",")})`, action: "default", tab: "wh-quality", size: 1000, deep: true }
    }
  );
  const orderList = [...orderData, ...Object.values(orders)];

  const onDrawerClose = () => {
    setDrawerState(() => ({ open: false, fabricItem: null }));
  };

  const onBiDrawerClose = () => {
    setBiDrawerState(() => ({ open: false, fabricItem: null }));
  };

  const onSubmit = (rows, _, onSubmitComplete) => {
    const data = rows
      .filter(row => !isEmpty(row.orderId))
      .map(row => ({
        ...row,
        part: row.effPartName,
        parentStyleColor: listFromCsv(row.color)?.length === 2 ? listFromCsv(row.color)[0] : undefined,
        styleColor: listFromCsv(row.color)?.length === 2 ? listFromCsv(row.color)[1] : row.color,
        id: !isEmpty(row?.id) ? row?.id : null,
        ledgerQty: row?.ledgerQty
      }));
    const options = {
      url: `invoices/${id}/fabric-items/${selectedKey}/ledgers`,
      method: "put",
      data: data
    };

    mutate(options, {
      onSuccess: () => {
        onSubmitComplete && onSubmitComplete(rows);
        setVersion(v => v + 1);
        setAssociating(false);
      },
      onFailure: err => {
        onSubmitComplete && onSubmitComplete();
        setVersion(v => v + 1);
        setAssociating(false);
      }
    });
  };

  const onBlanketIssue = (rows, _, onActionComplete) => {
    const data = {
      ...invoice,
      fabricItemList: rows.map(e => ({ id: e.id, blanketQty: e.blanketQty }))
    };

    const options = {
      url: `invoices/${id}`,
      method: "put",
      data,
      params: { action: "issue_blanket" }
    };

    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Blanket issued successfully."));
        onActionComplete && onActionComplete();
        setVersion(v => v + 1);
      },
      onFailure: () => onActionComplete && onActionComplete()
    });
  };

  const onChange = (name, value, row, form) => {
    const dataKey = name.split(".")[1];
    const rowIdx = Number(name.split(".")[0].replace("rows[", "").replace("]", ""));

    let order;
    let flowInfoList = [];
    if (dataKey === "orderId") {
      order = orderList?.find(o => value === o.id);
      if (!isEmpty(order)) {
        const whFlowInfoList = getWhFlowInfoList(order);
        flowInfoList = whFlowInfoList
          .filter(fi => !fi.isParent)
          .map(fi => ({ ...fi, parentColor: whFlowInfoList.find(e => e.flowInfoIds.includes(fi.parentId))?.color }));

        const productMap = flowInfoList.map(flowInfo => flowInfo.product).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
        const productList = Object.values(productMap);
        const styleList = getDistinctValues(flowInfoList.map(flowInfo => flowInfo.style)).map(s => ({ id: s, name: s }));
        const colorList = getDistinctObjectValues(flowInfoList.map(fi => ({ id: effectiveColor(fi, false), name: effectiveColor(fi, true) })));

        form.change(`${name.split(".")[0]}.productList`, productList);
        form.change(`${name.split(".")[0]}.styleList`, styleList);
        form.change(`${name.split(".")[0]}.colorList`, colorList);
      }
    }
    if (dataKey === "productId" || dataKey === "style" || dataKey === "color") {
      order = orderList?.find(o => row.orderId === o.id);
      if (!isEmpty(order)) {
        const fRow = { ...row, [dataKey]: value };

        const whFlowInfoList = getWhFlowInfoList(order);
        flowInfoList = whFlowInfoList
          .filter(fi => !fi.isParent)
          .map(fi => ({
            ...fi,
            productId: fi.product?.id,
            parentColor: whFlowInfoList.find(e => e.flowInfoIds.includes(fi.parentId))?.color
          }))
          .filter(fi => isEmpty(fRow[dataKey]) || fi[dataKey] === fRow[dataKey]);
      }
    }

    if (!isEmpty(flowInfoList)) {
      setFormValues(flowInfoList, name, form, { ...row, [dataKey]: value });
    }

    const rows = form.getState()?.values?.rows || [];
    const fRow = { ...rows[rowIdx], [dataKey]: value };

    //** Update Part and Part Choices **//
    if (dataKey === "orderId" || dataKey === "productId" || dataKey === "style" || dataKey === "color") {
      if (!isEmpty(fRow?.orderId) && !isEmpty(fRow?.productId) && !isEmpty(fRow?.style) && !isEmpty(fRow?.color)) {
        const order = orderList?.find(o => fRow.orderId === o.id);

        const whFlowInfoList = getWhFlowInfoList(order);
        flowInfoList = whFlowInfoList
          .filter(fi => !fi.isParent)
          .map(fi => ({ ...fi, parentColor: whFlowInfoList.find(e => e.flowInfoIds.includes(fi.parentId))?.color }));

        const flowInfo = flowInfoList?.find(fi => fi.product?.id === fRow.productId && fi.style === fRow.style && effectiveColor(fi) === fRow.color);
        const partList = flowInfo?.partList?.map(partMapper) || [];

        if (!isEmpty(partList)) {
          if (partList.length === 1) {
            form.change(`${name.split(".")[0]}.effPartName`, partList[0].id);
            form.change(`${name.split(".")[0]}.bomCu`, partList[0].bomCu);
            form.change(`${name.split(".")[0]}.uom`, partList[0].uom);
          } else if (partList.every(p => p.id !== fRow.effPartName)) {
            form.change(`${name.split(".")[0]}.effPartName`, null);
          }
          form.change(`${name.split(".")[0]}.partList`, partList);
        } else {
          form.change(`${name.split(".")[0]}.effPartName`, null);
          form.change(`${name.split(".")[0]}.partList`, []);
        }
      } else {
        form.change(`${name.split(".")[0]}.effPartName`, null);
        form.change(`${name.split(".")[0]}.partList`, []);
      }
    }

    //** Update Required Fabric **//
    const rowList = form.getState()?.values?.rows || [];
    const finalRow = { ...rowList[rowIdx], [dataKey]: value };
    if (!isEmpty(finalRow?.orderId) && !isEmpty(finalRow?.productId) && !isEmpty(finalRow?.style) && !isEmpty(finalRow?.color)) {
      const order = orderList?.find(o => finalRow.orderId === o.id);
      const whFlowInfoList = getWhFlowInfoList(order);
      flowInfoList = whFlowInfoList
        .filter(fi => !fi.isParent)
        .map(fi => ({ ...fi, parentColor: whFlowInfoList.find(e => e.flowInfoIds.includes(fi.parentId))?.color }));

      const flowInfo = flowInfoList?.find(fi => fi.product?.id === fRow.productId && fi.style === fRow.style && effectiveColor(fi) === fRow.color);

      if (!isEmpty(flowInfo)) {
        form.change(`${name.split(".")[0]}.orderQty`, flowInfo.orderQty);
        form.change(`${name.split(".")[0]}.extra`, flowInfo.extra);
      }
      if (!isEmpty(flowInfo) && !isEmpty(finalRow?.effPartName)) {
        const part = flowInfo.partList?.find(p => p.effPartName === finalRow.effPartName);
        if (!isEmpty(part?.ocu)) {
          const orderQty = flowInfo.orderQty || 0;
          const extra = flowInfo.extra || 0;

          const ocu = convert({
            input: part.ocu,
            uomIn: part.uom,
            uomOut: selectedRow?.uomLength,
            gsm: selectedRow?.orderingGsm,
            widthInCm: selectedRow?.width,
            conversionFactorList
          });
          const bomCu = convert({
            input: part.ocu,
            uomIn: part.uom,
            uomOut: UOM.METER.value,
            gsm: selectedRow?.orderingGsm,
            widthInCm: selectedRow?.width,
            conversionFactorList
          });

          const fabricRequired = orderQty * ocu;
          const fabricRequiredInMeter = orderQty * bomCu;
          const extraFabricRequired = orderQty * (1 + 0.01 * extra) * ocu;
          const extraFabricRequiredInMeter = orderQty * (1 + 0.01 * extra) * bomCu;

          form.change(`${name.split(".")[0]}.bomCu`, ocu);
          form.change(`${name.split(".")[0]}.uom`, selectedRow?.uomLength);
          form.change(`${name.split(".")[0]}.fabricRequired`, Number(toFixed(fabricRequired, 1)));
          form.change(`${name.split(".")[0]}.extraFabricRequired`, Number(toFixed(extraFabricRequired, 1)));
          form.change(`${name.split(".")[0]}.fabricRequiredInMeter`, Number(toFixed(fabricRequiredInMeter, 1)));
          form.change(`${name.split(".")[0]}.extraFabricRequiredInMeter`, Number(toFixed(extraFabricRequiredInMeter, 1)));

          mutate(
            {
              url: `ledgers`,
              method: "get",
              params: {
                search: `type=='OUT';outType=='Bulk';order.id==${order.id};productId==${flowInfo.product?.id};style=='${flowInfo.style}';styleColor=='${flowInfo.color}';part=='${part.effPartName}'`
              }
            },
            {
              onSuccess: ledgerList => {
                const associatedQty = ledgerList.reduce((acc, l) => acc + l.ledgerQty, 0);
                form.change(`${name.split(".")[0]}.alreadyAssociatedQty`, Number(toFixed(associatedQty, 1)));
              }
            }
          );
        }
      }
    }
  };

  const columns = useDeepMemo(() => {
    let colList = [
      { dataKey: "id", title: "ID" },
      { dataKey: "itemCode", title: "ERP Item Code" },
      { dataKey: "color", title: "Fabric Color", render: (value, shortValue, row) => row?.effFabColor || "-", filterOptions: { filter: true } },
      { dataKey: "txn", title: "Transfer Txn", render: value => value || "-" },
      { dataKey: "itemDesc", title: "Item Description", render: value => value || "-" },
      { dataKey: "articleName", title: "Article Name", render: value => value || "-" },
      { dataKey: "classification", title: "Fabric\nClassification", filterOptions: { filter: true }, render: value => value || "-" },
      { dataKey: "construction", title: "Count\nConstruction", render: value => value || "-" },
      { dataKey: "content", title: "Content", render: value => value || "-" },
      { dataKey: "rollForm", title: "Roll Form" },
      { dataKey: "colorShade", title: "Color Shade", render: (value, shortValue) => shortValue || "-", filterOptions: { filter: true } },
      { dataKey: "fpo", title: "FPO No.", filterOptions: { filter: true } },
      {
        dataKey: "orderQty",
        title: "Fabric Order\nQty",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomLength]}`)
      },
      {
        dataKey: "invoiceQty",
        title: "Invoice Qty",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomLength]}`)
      },
      {
        dataKey: "orderingWidth",
        title: "Ordering\nWidth",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`)
      },
      { dataKey: "orderingGsm", title: "OrderingGSM" },
      { dataKey: "orderingCsv", title: "OrderingCSV" },
      { dataKey: "orderingWarpShrinkage", title: "Ordering\nWarp Shrinkage" },
      { dataKey: "orderingWeftShrinkage", title: "Ordering\nWeft Shrinkage" },
      { dataKey: "grn", title: "GRN" },
      { dataKey: "grnDate", title: "GRN Date", render: value => (isEmpty(value) ? "" : formatDate(value, utc_offset)) },
      {
        dataKey: "grnQty",
        title: "GRN Qty",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomLength]}`)
      },
      { dataKey: "rollCount", title: "Roll Count", render: value => value || 0 },
      {
        dataKey: "blanketQty",
        title: "Blanket Qty",
        inputType: "text-input",
        disabled: row => isEmpty(row.rollCount) || row?.rollCount === 0,
        options: { type: "number" },
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomLength]}`),
        validate: [minValue(0, "Must be at least 0.")]
      },
      {
        dataKey: "nominatedSupplier",
        title: "Nominated Supplier",
        render: value => (value === true ? "Yes" : value === false ? "No" : "-")
      },
      { dataKey: "warehouse", title: "Warehouse", render: value => allWhList?.find(e => e.code === value)?.name, filterOptions: { filter: true } }
    ];

    if (invoice?.fabricItemList?.every(fi => isEmpty(fi.parentId))) {
      colList = colList.filter(c => c.dataKey !== "txn");
    }

    return colList.filter(c => c.dataKey === "id" || c.dataKey === "warehouse" || c.dataKey === "txn" || fabricItemFields.includes(c.dataKey));
  }, [utc_offset, fabricItemFields, allWhList, invoice]);

  useDeepEffect(() => {
    const fetchOrderChoices = (searchText, rows) => {
      const ids = Array.isArray(rows) ? getDistinctValues(rows.map(row => row.orderId).filter(id => !isEmpty(id))) : [];
      return new Promise(resolve => {
        const options = {
          url: "orders",
          method: "get",
          params: {
            deep: true,
            tab: "wh-store",
            search: isEmpty(searchText)
              ? isEmpty(ids)
                ? ""
                : `id=in=(${ids.join(",")})`
              : `(poRef=='*${searchText}*',customerPoRef=='*${searchText}*')`
          }
        };
        mutate(options, {
          onSuccess: orderList => {
            const choices = orderList.map(({ id, poRef, customerPoRef }) => ({
              id,
              name: `${poRef}${isEmpty(customerPoRef) ? "" : ` (${customerPoRef})`}`
            }));
            setOrders(prev => ({ ...prev, ...orderList.reduce((acc, order) => ({ ...acc, [order.id]: order }), {}) }));
            resolve(choices);
          }
        });
      });
    };

    let columns = [
      {
        dataKey: "orderId",
        title: "PO Ref.",
        linkPath: row => history.push(`/order-mngmt/orders/${row.orderId}/view`),
        inputType: "auto-complete-input",
        fetchChoices: fetchOrderChoices,
        validate: [required()],
        options: { style: { width: 200 } },
        disabled: row => !isEmpty(row.order),
        render: (value, shortValue, row) => row?.poRef || "-"
      },
      {
        dataKey: "productId",
        title: "Product",
        inputType: "select-input",
        validate: [required()],
        choices: ({ row }) => (!isEmpty(row) ? row?.productList || [] : []),
        render: (value, shortValue, row) => row?.productName || "-",
        filterOptions: { filter: true }
      },
      {
        dataKey: "style",
        title: "Style",
        inputType: "select-input",
        validate: [required()],
        choices: ({ row }) => (!isEmpty(row) ? row?.styleList || [] : [])
      },
      {
        dataKey: "color",
        title: "Style Color",
        inputType: "select-input",
        validate: [required()],
        choices: ({ row }) => (!isEmpty(row) ? row?.colorList || [] : []),
        filterOptions: { filter: true },
        render: value => (`${value}`.includes(",") ? getDistinctValues(listFromCsv(`${value}`)).join(", ") : `${value}`)
      },
      {
        dataKey: "effPartName",
        title: "Part",
        inputType: "select-input",
        validate: [required()],
        choices: ({ row }) => (!isEmpty(row) ? row?.partList || [] : []),
        render: (value, shortValue, row) => row?.effPartName || "-",
        filterOptions: { filter: true }
      },
      {
        dataKey: "bomCu",
        title: "BOM CU",
        align: "right",
        render: (value, _, row) => (!isEmpty(value) ? `${toFixed(value, 3)} ${UomCodeFromValue[row.uom]}` : "-")
      },
      {
        dataKey: "orderQty",
        title: "Order Qty",
        align: "right",
        render: value => value || "-"
      },
      {
        dataKey: "extra",
        title: "Extra (%)",
        align: "right",
        render: (value, _, row) => (isEmpty(value) ? "0%" : `${value}%`)
      },
      {
        dataKey: "fabricRequired",
        title: "Required Fabric",
        align: "right",
        render: (value, _, row) => (!isEmpty(value) ? `${value} ${UomCodeFromValue[row.uom]}` : "-")
        // render: (value, _, row) =>
        //   !isEmpty(value) ? `${value} ${UomCodeFromValue[row.uom]} ${row.uom !== UOM.METER.value ? `(${row.fabricRequiredInMeter} m)` : ""}` : "-"
      },
      {
        dataKey: "extraFabricRequired",
        title: "Required Fabric (with extra)",
        align: "right",
        render: (value, _, row) => (!isEmpty(value) ? `${value} ${UomCodeFromValue[row.uom]}` : "-")
        // render: (value, _, row) =>
        //   !isEmpty(value)
        //     ? `${value} ${UomCodeFromValue[row.uom]} ${row.uom !== UOM.METER.value ? `(${row.extraFabricRequiredInMeter} m)` : ""}`
        //     : "-"
      },
      {
        dataKey: "alreadyAssociatedQty",
        title: "Already Associated Qty",
        align: "right",
        render: (value, _, row) => (!isEmpty(value) ? `${value} ${UomCodeFromValue[row.uom]}` : "-")
      },
      {
        dataKey: "ledgerQty",
        title: "Association Qty",
        inputType: "text-input",
        align: "right",
        render: (value, _, row) => (!isEmpty(value) ? `${toFixed(value)} ${UomCodeFromValue[row.uom]}` : "-"),
        options: { type: "number" }
      },
      {
        dataKey: "allocatedQty",
        title: "Allocated Qty",
        align: "right",
        render: (value, _, row) => (!isEmpty(value) ? `${toFixed(value, 3)} ${UomCodeFromValue[row.uom]}` : "-")
      }
    ];

    if (roles?.includes(Role.SUPER_USER) || roles?.includes(Role.MASTER)) {
      columns.push({
        dataKey: "extRemarks",
        title: "Ext Remarks",
        inputType: "text-input",
        align: "center",
        disabled: () => true
      });
    }

    setAssociationColumns(columns);
  }, [orderList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(invoice?.fabricItemList)) return [];
    const rows = invoice?.fabricItemList
      .sort((a, b) => a.serial - b.serial)
      .map(fabricItem => {
        const fabric = fabrics.find(f => fabricItem?.fabric?.id === f.id);
        const transferLedger = invLedgerList.find(l => l.type === LedgerType.ITEM_TRANSFER && l.toFabricItem?.id === fabricItem?.id);
        return {
          ...fabric,
          ...fabricItem,
          txn: transferLedger?.id,
          fabricId: fabric?.id,
          colorId: fabric?.colorList?.find(f => fabricItem.color === f.color)?.id,
          colorList: fabric?.colorList,
          transferLedger
        };
      });
    setSelectedKey(rows.filter(e => isTrue(e.isLeaf))[0]?.id);
    return rows;
  }, [invoice, fabrics, invLedgerList]);

  const selectedRow = rows.find(e => e.id === selectedKey);

  useDeepEffect(() => {
    const ledgers = ledgerList.filter(l => l.outType === "Bulk");
    if (isEmpty(ledgers)) {
      const order = orderList.find(e => e.id === selectedRow?.order?.id);
      if (!isEmpty(order)) {
        const whFlowInfoList = getWhFlowInfoList(order);
        const flowInfoList = whFlowInfoList
          .filter(fi => !fi.isParent)
          .map(fi => ({ ...fi, parentColor: whFlowInfoList.find(e => e.flowInfoIds.includes(fi.parentId))?.color }));
        const productMap = flowInfoList.map(flowInfo => flowInfo.product).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
        const productList = Object.values(productMap);
        const styleList = getDistinctValues(flowInfoList.map(flowInfo => flowInfo.style)).map(s => ({ id: s, name: s }));
        const colorList = getDistinctObjectValues(flowInfoList.map(fi => ({ id: effectiveColor(fi, false), name: effectiveColor(fi, true) })));
        const partList = flowInfoList.length === 1 ? flowInfoList[0]?.partList?.map(partMapper) : [];

        setAssociationRows([
          {
            orderId: order?.id,
            order: order,
            productList,
            styleList,
            colorList,
            partList
          }
        ]);
      } else {
        setAssociationRows([]);
      }
    } else {
      const rows = ledgers.map(ledger => {
        const order = orderList.find(e => e.id === ledger.orderId);
        const whFlowInfoList = getWhFlowInfoList(order);
        const flowInfoList = whFlowInfoList
          .filter(fi => !fi.isParent)
          .map(fi => ({ ...fi, parentColor: whFlowInfoList.find(e => e.flowInfoIds.includes(fi.parentId))?.color }));

        const flowInfo = flowInfoList?.find(
          fi =>
            fi.product?.id === ledger.productId &&
            fi.style === ledger.style &&
            fi.color === ledger.styleColor &&
            (isEmpty(fi.parentColor) || fi.parentColor === ledger.parentStyleColor)
        );

        const productMap = flowInfoList.map(flowInfo => flowInfo.product).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
        const productList = Object.values(productMap);
        const styleList = getDistinctValues(flowInfoList.map(flowInfo => flowInfo.style)).map(s => ({ id: s, name: s }));
        const colorList = getDistinctObjectValues(flowInfoList.map(fi => ({ id: effectiveColor(fi, false), name: effectiveColor(fi, true) })));
        const partList = flowInfo?.partList?.map(partMapper);
        const part = flowInfo?.partList?.find(p => p.effPartName === ledger.part) || {};

        const orderQty = flowInfo?.orderQty || 0;
        const extra = flowInfo?.extra || 0;
        const bomCu = convert({
          input: part.ocu,
          uomIn: part.uom,
          uomOut: selectedRow?.uomLength,
          gsm: selectedRow?.orderingGsm,
          widthInCm: selectedRow?.width,
          conversionFactorList
        });
        const fabricRequired = isEmpty(bomCu) ? null : orderQty * bomCu;
        const extraFabricRequired = isEmpty(bomCu) ? null : orderQty * (1 + 0.01 * extra) * bomCu;
        const allocatedQty =
          part?.uom === UOM.KILOGRAM.value || part?.uom === UOM.GRAM.value
            ? ledger.allocatedWeight
            : convert({
                input: ledger.allocatedLength,
                uomIn: UOM.METER.value,
                uomOut: selectedRow?.uomLength,
                gsm: selectedRow?.orderingGsm,
                widthInCm: selectedRow?.width,
                conversionFactorList
              });
        return {
          ...ledger,
          poRef: flowInfo?.poRef,
          productId: flowInfo?.product.id,
          productName: flowInfo?.product?.name,
          style: flowInfo?.style,
          color: effectiveColor(flowInfo, false),
          effPartName: part?.effPartName,
          bomCu,
          uom: selectedRow?.uomLength,
          orderQty,
          extra,
          fabricRequired: Number(toFixed(fabricRequired, 1)),
          extraFabricRequired: Number(toFixed(extraFabricRequired, 1)),
          allocatedQty: !isEmpty(allocatedQty) ? Number(toFixed(allocatedQty, 1)) : undefined,
          qty: ledger.qty,
          productList,
          styleList,
          colorList,
          partList
        };
      });
      setAssociationRows(rows);
    }
  }, [ledgerList, selectedRow, orderList, conversionFactorList]);

  const onInlineActionClick = (event, action, row, onActionComplete) => {
    if (action === "upload-blanket-issue") {
      setBiDrawerState({ open: true, fabricItem: row });
    } else {
      setDrawerState({ open: true, fabricItem: row });
    }
  };

  const onFooterActionClick = (event, action, rows, onActionComplete) => {
    if (action === "edit") {
      setAssociating(true);
    } else {
      setAssociating(false);
    }
  };

  const onRowAdd = (rows, rowIdx) => {
    if (!isEmpty(selectedRow.order)) {
      const row = !isEmpty(rowIdx) ? rows[rowIdx] : rows[rows?.length - 1];
      return {
        orderId: selectedRow?.order?.id,
        order: selectedRow?.order,
        productList: row.productList,
        styleList: row.styleList,
        colorList: row.colorList,
        partList: row.partList
      };
    }
  };

  const onRowClick = row => {
    if (associating || isFalse(row.isLeaf)) return;
    setSelectedKey(row.id);
  };

  const footerActions = hasPrivilege(roles, hasAccess, ResourceId.GRN, Scope.WRITE)
    ? [{ name: "edit", tooltip: "Issue Blanket", options: { variant: "outlined", color: "primary" } }]
    : [];

  const totalGrnQty = rows?.filter(e => e.id === selectedKey).reduce((acc, fi) => acc + fi.grnQty, 0) || 0;
  const totalAssociatedQty = ledgerList.filter(l => l.outType === "Bulk").reduce((acc, l) => acc + l.ledgerQty, 0) || 0;

  const diffQty = totalGrnQty - totalAssociatedQty;

  const inlineActions = row => {
    let actions = [];
    if (row?.rollCount > 0) {
      actions = [{ name: "view", tooltip: "Packing List", icon: <ViewIcon /> }];
    } else if (hasPrivilege(roles, hasAccess, ResourceId.GRN, Scope.WRITE) && !disableEdit) {
      actions = [{ name: "upload", tooltip: "Upload Packing List", icon: <UploadIcon /> }];
    }
    if (hasPrivilege(roles, hasAccess, ResourceId.GRN, Scope.WRITE) && !disableEdit) {
      actions = [...actions, { name: "upload-blanket-issue", tooltip: "Upload Blanket Issue", icon: <UploadIcon /> }];
    }

    return actions;
  };

  return (
    <div>
      {/** Drawer For Supplier Rolls Upload Against Fabric Item */}
      <PackingListDrawer
        open={drawerState?.open || false}
        invoice={invoice}
        fabricItem={drawerState?.fabricItem}
        roles={roles}
        hasAccess={hasAccess}
        onDrawerClose={onDrawerClose}
        onRefresh={() => setVersion(v => v + 1)}
      />

      <BlanketIssueDrawer
        open={biDrawerState?.open || false}
        invoice={invoice}
        fabricItem={biDrawerState?.fabricItem}
        roles={roles}
        hasAccess={hasAccess}
        onDrawerClose={onBiDrawerClose}
        onRefresh={() => setVersion(v => v + 1)}
      />

      {/** Header Element */}
      <PageHeader title="resources.invoices.pageHeader.view" titleArgs={{ _: "View Invoice" }} />

      <div className={classes.content}>
        {/* Invoice Details */}
        <Card>
          <CardHeader title="Invoice Details" />
          <Divider />
          <CardContent>
            <SimpleShowLayout basePath={basePath} resource={resource} record={invoice} i18nKey={i18nKey} footer={false}>
              {invoiceFields.includes("supplier") && (
                <FunctionField source="supplier" render={record => record?.supplier?.name} {...fieldOptions(3)} />
              )}
              {invoiceFields.includes("invoiceNo") && <TextField source="invoiceNo" {...fieldOptions(3)} />}
              {invoiceFields.includes("erpInvoiceNo") && <TextField source="erpInvoiceNo" {...fieldOptions(3)} />}
              {invoiceFields.includes("invoiceQty") && (
                <FunctionField
                  source="invoiceQty"
                  render={record =>
                    isEmpty(record?.fabricItemList) ? "" : toFixed(record.fabricItemList.reduce((acc, e) => acc + (e.invoiceQty || 0), 0))
                  }
                  {...fieldOptions(3)}
                />
              )}
            </SimpleShowLayout>
          </CardContent>
        </Card>

        {/* Fabric Item Details */}
        <Card style={{ marginTop: "1.5em" }}>
          <MuiTable
            title={<CardHeader title="Fabric Item Details" style={{ marginLeft: 0 }} />}
            searchable
            searchKeys={["itemCode", "itemDesc", "articleName", "poRef", "fpo"]}
            columns={columns}
            rows={rows}
            editable={!isEmpty(footerActions) && !disableBIEdit}
            pageable={rows.length > 10}
            defaultExpanded
            inlineActions={inlineActions}
            onInlineActionClick={onInlineActionClick}
            footerActions={footerActions}
            onSubmit={onBlanketIssue}
            fontSize={16}
            onRowClick={onRowClick}
            rowStyle={({ row }) => (selectedKey === row?.id ? { backgroundColor: "#BFDDF8" } : {})}
            i18nKey="resources.fabricItems.fields"
          />
        </Card>

        {/* Fabric Assocaition Details */}
        {!isEmpty(selectedKey) && (
          <Card style={{ marginTop: "1.5em" }}>
            <MuiTable
              key={`${JSON.stringify(associationRows)}-${vesrion}`}
              title={
                <CardHeader
                  title={`Fabric Association Details (Balance: ${diffQty} ${UomCodeFromValue[selectedRow?.uomLength]})`}
                  style={{ marginLeft: 0 }}
                />
              }
              searchable
              searchKeys={["poRef", "style"]}
              columns={associationColumns}
              rows={associationRows}
              editable={hasPrivilege(roles, hasAccess, ResourceId.INVOICE, Scope.UPDATE) && !disableEdit}
              showEditableActions
              enableRowAddition
              onRowAdd={onRowAdd}
              rowAddCount={1}
              onSubmit={onSubmit}
              onChange={onChange}
              fontSize={16}
              onFooterActionClick={onFooterActionClick}
              i18nKey="resources.invoices.fields.association"
            />
          </Card>
        )}

        {/* Footer */}
        <PageFooter className={classes.pageFooter}>
          <BackButton className={classes.button} label="Back" variant="outlined" color="primary" />
          {hasPrivilege(roles, hasAccess, ResourceId.INVOICE, Scope.UPDATE) && !disableEdit && (
            <EditButton className={classes.button} variant="outlined" resource={resource} basePath={basePath} record={invoice} />
          )}

          {hasPrivilege(roles, hasAccess, ResourceId.INVOICE, Scope.DELETE) && !disableDelete && (
            <DeleteButton
              className={classes.button}
              variant="outlined"
              disabled={isTrue(invoice?.qcStarted || invoice?.rollAllocated)}
              resource={resource}
              basePath={basePath}
              record={invoice}
              redirect="home"
              recordName="Invoice"
            />
          )}
          {hasPrivilege(roles, hasAccess) && (
            <Button label={`js.action.history`} component={Link} to={`/warehouse/invoices/${id}/history`} variant="outlined" color="primary">
              <HistoryIcon />
            </Button>
          )}
        </PageFooter>
      </div>
    </div>
  );
};

export default InvoiceView;
